import { useRef } from 'react';
import { Badge, Card, Form, Table, Spinner, Button } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TablePageLayout } from '../components/TablePageLayout';
import { StatusBadge } from '../components/StatusBadge';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchOrders, ORDERS_PAGE_SIZE } from '../api/orderApi';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const detailBaseByKind = {
  po: '/agent/po',
  so: '/agent/so',
};

const stateColors = {
  draft: 'secondary',
  sent: 'info',
  purchase: 'success',
  sale: 'success',
  done: 'primary',
  cancel: 'danger',
};

const invoiceStatusColors = {
  no: 'secondary',
  to_invoice: 'warning',
  invoiced: 'success',
  upselling: 'info',
};

const stateLabels = {
  draft: 'Draft',
  sent: 'Sent',
  purchase: 'Purchase Order',
  sale: 'Sales Order',
  done: 'Locked',
  cancel: 'Cancelled',
};

const invoiceStatusLabels = {
  no: 'Nothing to Invoice',
  'to invoice': 'To Invoice',
  invoiced: 'Fully Invoiced',
  upselling: 'Upselling Opportunity',
};


export function OrderListPage({ title, kind }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  const setQuery = (newQuery) => {
    if (newQuery) {
      setSearchParams({ q: newQuery }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const detailBase = detailBaseByKind[kind];
  const loadMoreRef = useRef(null);

  const effectiveQuery = query.length >= 4 ? query : '';
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey: ['orders', kind, effectiveQuery],
    queryFn: fetchOrders,
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.reduce((sum, page) => sum + page.orders.length, 0);
      // Assuming the API returns the total number of orders in each response if needed, 
      // but based on the example it only returns the 'orders' array.
      // If the last page has fewer than the limit, we can assume there are no more pages.
      return lastPage.orders.length === ORDERS_PAGE_SIZE ? currentCount : undefined;
    },
    initialPageParam: 0,
  });

  const orders = data?.pages.flatMap((page) => page.orders) || [];

  const { containerRef, handleScroll } = useScrollRestoration(
    `orderListScroll_${kind}_${effectiveQuery}`,
    isFetched
  );

  useInfiniteScroll({
    loadMoreRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef: containerRef,
    dependencyArray: [orders.length]
  });

  return (
    <TablePageLayout
      title={title}
      description="Searchable order list."
      action={(
        <Form.Control
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={`Search ${title.toLowerCase()}`}
          className="w-100"
          style={{ maxWidth: '18rem' }}
        />
      )}
      isLoading={isLoading && orders.length === 0}
      isError={isError}
      error={error}
      containerRef={containerRef}
      onScroll={handleScroll}
    >
      <Table hover className="mb-0 align-middle sticky-header">
        <thead>
          <tr>
            <th>Reference</th>
            <th>Order date</th>
            <th>Responsible</th>
            <th>Total</th>
            <th>Status</th>
            <th>Invoice status</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && !isLoading ? (
            <tr>
              <td colSpan="6" className="text-center py-4 text-muted">
                No orders found.
              </td>
            </tr>
          ) : (
            orders.map((order) => (
              <tr
                key={order.mid}
                onClick={() => navigate(`${detailBase}/${order.name}`)}
                style={{ cursor: 'pointer' }}
              >
                <td><Link to={`${detailBase}/${order.name}`} onClick={(e) => e.stopPropagation()}>{order.name}</Link></td>
                <td>{formatDate(order.date_order)}</td>
                <td>{order.user_id.name}</td>
                <td>{formatCurrency(order.amount_total)}</td>
                <td>
                  <StatusBadge value={order.state} colors={stateColors} labels={stateLabels} />
                </td>
                <td>
                  <StatusBadge value={order.invoice_status} colors={invoiceStatusColors} labels={invoiceStatusLabels} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="text-center py-4 border-top">
          {isFetchingNextPage ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Loading more...
            </>
          ) : (
            <Button variant="link" onClick={() => fetchNextPage()}>
              Load more
            </Button>
          )}
        </div>
      )}
    </TablePageLayout>
  );
}
